import React, { useEffect, useState } from "react";
import { ProgressBar } from "react-loader-spinner";
import Header from "units/dashboard/components/Header";
import { SidebarProvider, useSidebar } from "units/dashboard/context/SidebarProvider";
import { useSelector } from "react-redux";
import Footer from "units/dashboard/components/Footer";
import TransactionsTable from "./components/TransactionsTable";
import { transaction } from "./services/transactionServices";
import UserSidebar from "../components/UserSidebar";
import { Receipt, Wallet, TrendingUp, CreditCard, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

/* ================================================================
 *  Main
 * ================================================================ */
const UserTransactionListInner = () => {
  const { t, i18n } = useTranslation(["user"]);
  const isEnglish = i18n.language === "en";

  const { isSidebarOpen, toggleSidebar } = useSidebar();
  const [loading, setLoading] = useState(false);
  const [transactionList, setTransactionList] = useState([]);
  const accessToken = useSelector((state) => state.auth.accessToken);

  /* ---------- بستن سایدبار موبایل هنگام resize به دسکتاپ ---------- */
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024 && isSidebarOpen) toggleSidebar();
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isSidebarOpen, toggleSidebar]);

  const userTransactionList = async () => {
    setLoading(true);
    try {
      const res = await transaction(accessToken);
      if (res?.ok) setTransactionList(res?.data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    userTransactionList();
  }, []);

  /* ---------- خلاصه‌ی آماری ---------- */
  const stats = React.useMemo(() => {
    const list = Array.isArray(transactionList) ? transactionList : [];
    const success = list.filter((t) => t.status === "success" || t.status === "done");
    const failed = list.filter((t) => t.status === "fail" || t.status === "failed");
    const pending = list.filter((t) => t.status === "pending");

    const totalSuccessAmount = success.reduce(
      (sum, t) => sum + Number(t.amount || 0),
      0
    );

    return {
      total: list.length,
      successCount: success.length,
      failedCount: failed.length,
      pendingCount: pending.length,
      totalSuccessAmount,
    };
  }, [transactionList]);

  if (loading) {
    return (
      <div className="h-screen flex flex-col overflow-hidden bg-gradient-to-br from-slate-50 via-white to-teal-50/40 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
        <nav className="shrink-0 z-30 h-[70px] flex items-center border-b border-teal-500/20 dark:border-teal-400/15 bg-white/85 dark:bg-slate-900/85 backdrop-blur-xl">
          <Header />
        </nav>
        <div className="flex-1 flex items-center justify-center">
          <ProgressBar visible height="80" width="80" barColor="#14B8A6" borderColor="#FCD34D" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gradient-to-br from-slate-50 via-white to-teal-50/40 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">

      {/* ================= Header ================= */}
      <nav className="shrink-0 z-30 h-[70px] flex items-center border-b border-teal-500/20 dark:border-teal-400/15 bg-white/85 dark:bg-slate-900/85 backdrop-blur-xl shadow-[0_4px_30px_-12px_rgba(15,118,110,0.35)] print:hidden">
        <Header />
      </nav>

      {/* ================= Body ================= */}
      <div className="flex-1 min-h-0 relative">

        {isSidebarOpen && (
          <div
            onClick={toggleSidebar}
            className="lg:hidden fixed top-[70px] inset-x-0 bottom-0 z-30 bg-slate-900/50 dark:bg-black/60 backdrop-blur-sm transition-opacity duration-300"
            aria-hidden="true"
          />
        )}

        <aside
          className={`
            fixed top-[70px] bottom-0 rtl:right-0 ltr:left-0 w-64 z-40
            transform transition-transform duration-300 ease-out
            ${isSidebarOpen ? "translate-x-0" : "rtl:translate-x-full ltr:-translate-x-full"}
            lg:!translate-x-0
            rtl:border-l ltr:border-r border-teal-500/10 dark:border-teal-400/10
            bg-gradient-to-b from-white/95 via-teal-50/60 to-white/95
            dark:from-slate-900/95 dark:via-slate-900/98 dark:to-slate-900/95
            backdrop-blur-xl shadow-[0_0_40px_-10px_rgba(15,118,110,0.4)] lg:shadow-none
            print:hidden overflow-hidden
          `}
        >
          <UserSidebar />
        </aside>

        <main className="h-full flex flex-col lg:rtl:pr-64 lg:ltr:pl-64 min-w-0">
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden custom-scrollbar">
            <div className="px-3 sm:px-4 md:px-6 py-5 sm:py-6">

              {/* ============ Page Header ============ */}
              <div className="max-w-5xl mx-auto mb-5">
                <div className="flex items-center gap-2.5">
                  <span className="shrink-0 flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-amber-300/30 to-teal-400/20 border border-amber-300/40">
                    <Receipt size={20} className="text-amber-500" />
                  </span>
                  <div className="min-w-0">
                    <h1
                      className={`
                        text-lg sm:text-2xl font-bold
                        bg-gradient-to-l from-teal-700 via-teal-600 to-amber-500
                        dark:from-amber-200 dark:via-teal-300 dark:to-teal-400
                        bg-clip-text text-transparent
                        ${isEnglish ? "font-sans" : ""}
                      `}
                    >
                      {t("transactionList.pageTitle")}
                    </h1>
                    <p
                      className={`
                        text-[11px] sm:text-xs text-slate-500 dark:text-slate-400 mt-1
                        ${isEnglish ? "font-sans" : ""}
                      `}
                    >
                      {t("transactionList.pageSubtitle")}
                    </p>
                  </div>
                </div>
              </div>

              {/* ============ Table Section ============ */}
              <div className="max-w-5xl mx-auto relative rounded-2xl bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border border-teal-500/15 dark:border-teal-400/15 shadow-[0_10px_40px_-20px_rgba(15,118,110,0.4)] dark:shadow-[0_10px_40px_-20px_rgba(0,0,0,0.85)] overflow-hidden">

                <div className="pointer-events-none absolute top-0 inset-x-0 h-[2px] bg-gradient-to-l from-transparent via-amber-400/70 to-transparent" />

                <div className="pointer-events-none absolute inset-0 rounded-2xl overflow-hidden">
                  <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-amber-300/10 blur-3xl dark:bg-amber-400/5" />
                  <div className="absolute -bottom-16 -left-16 h-40 w-40 rounded-full bg-teal-400/10 blur-3xl dark:bg-teal-500/5" />
                </div>

                <div className="relative p-4 sm:p-5">
                  <TransactionsTable
                    transactionList={transactionList}
                    isEnglish={isEnglish}
                  />
                </div>
              </div>

              <div className="max-w-5xl mx-auto mt-8">
                <Footer />
              </div>
            </div>
          </div>
        </main>
      </div>

      <style>{`
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(20, 184, 166, 0.55) transparent;
          scroll-behavior: smooth;
        }
        .custom-scrollbar::-webkit-scrollbar { width: 10px; height: 10px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; border-radius: 999px; margin: 6px 0; }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          border-radius: 999px; border: 2px solid transparent; background-clip: padding-box;
          background-image: linear-gradient(180deg, rgba(20,184,166,0.55), rgba(45,212,191,0.7), rgba(251,191,36,0.55));
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-image: linear-gradient(180deg, rgba(20,184,166,0.9), rgba(45,212,191,1), rgba(251,191,36,0.9));
          box-shadow: 0 0 12px -2px rgba(20,184,166,0.7);
        }
        .dark .custom-scrollbar { scrollbar-color: rgba(251,191,36,0.45) transparent; }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background-image: linear-gradient(180deg, rgba(20,184,166,0.55), rgba(94,234,212,0.65), rgba(251,191,36,0.65));
        }
      `}</style>
    </div>
  );
};

/* ================================================================
 *  StatTile helper
 * ================================================================ */
const StatTile = ({ icon: Icon, label, value, tone = "teal", isEnglish = false }) => {
  const tones = {
    teal: {
      wrap: "border-teal-500/20 dark:border-teal-400/15 hover:border-amber-300/60",
      icon: "from-teal-400/40 to-teal-600/20 border-teal-400/50 text-teal-700 dark:text-teal-300",
      value: "text-teal-700 dark:text-teal-300",
    },
    emerald: {
      wrap: "border-emerald-500/20 dark:border-emerald-400/15 hover:border-amber-300/60",
      icon: "from-emerald-400/40 to-teal-600/20 border-emerald-400/50 text-emerald-700 dark:text-emerald-300",
      value: "text-emerald-700 dark:text-emerald-300",
    },
    amber: {
      wrap: "border-amber-500/20 dark:border-amber-400/15 hover:border-amber-300/60",
      icon: "from-amber-300/40 to-amber-500/20 border-amber-300/50 text-amber-700 dark:text-amber-300",
      value: "text-amber-700 dark:text-amber-300",
    },
    rose: {
      wrap: "border-rose-500/20 dark:border-rose-400/15 hover:border-amber-300/60",
      icon: "from-rose-400/40 to-rose-600/20 border-rose-400/50 text-rose-700 dark:text-rose-300",
      value: "text-rose-700 dark:text-rose-300",
    },
  };
  const t = tones[tone];

  return (
    <div
      className={`
        group relative overflow-hidden
        flex items-center gap-2.5
        rounded-2xl
        bg-white/60 dark:bg-white/5
        backdrop-blur-md
        border ${t.wrap}
        p-3
        transition-all duration-300
        hover:bg-amber-300/5
        hover:scale-[1.02]
      `}
    >
      <span
        className={`
          shrink-0 flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center
          rounded-xl
          bg-gradient-to-br ${t.icon}
          border
        `}
      >
        <Icon size={16} />
      </span>

      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 tracking-wide truncate">
          {label}
        </p>
        <p
          className={`
            text-lg sm:text-xl font-extrabold tabular-nums ${t.value} mt-0.5
            ${isEnglish ? "font-sans" : ""}
          `}
        >
          {Number(value || 0).toLocaleString(isEnglish ? "en-US" : "fa-IR")}
        </p>
      </div>
    </div>
  );
};

const UserTransactionList = () => (
  <SidebarProvider>
    <UserTransactionListInner />
  </SidebarProvider>
);

export default UserTransactionList;